import { PurchaseInvoiceService } from "@/api/services/transactionService";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface PurchaseInvoiceDetail {
  purchaseId: string;
  account: string;
  amount: number;
  gstTaxableValue: number;
  igst: string;
  sac: string;
  tds: string;
  narration: string;
}

export interface PurchaseInvoicePayment {
  purchaseId: string;
  paymentTypeId: string;
  account: string;
  amount: number;
  chequeNo: string;
  bankName:string;
  chequeDate:string;
}

export interface PurchaseInvoiceDocument {
  PurchaseId: string;
  Remark: string;
  File?: File;
  FileName: string;
  fileName: string;
  remark: string;
  purchaseId: string;
}

export interface PurchaseInvoiceInfo {
  id?: string;
  location: string;
  date: string;
  billDate: string;
  party: string;
  partyName: string;
  stateOfSupplier: string;
  stateOfReciever: string;
  gstin: string;
  taxClassification: string;
  reverseCharge: string;
  remark: string;
  purchaseInvoiceDetail: PurchaseInvoiceDetail[];
  purchaseInvoicePayment: PurchaseInvoicePayment[];
  purchaseDocument: PurchaseInvoiceDocument;
  totalAmount?:number;
}

interface TabState {
  activeTab: string;
}
interface PurchaseInvoiceFormState {
  info: PurchaseInvoiceInfo;
  editInfo: PurchaseInvoiceInfo;
  error: string | undefined;
}

const initialInfo: PurchaseInvoiceInfo = {
  id: "",
  location: "",
  date: "",
  billDate: "",
  party: "",
  partyName: "",
  stateOfSupplier: "",
  stateOfReciever: "",
  gstin: "",
  taxClassification: "",
  reverseCharge: "",
  remark: "",
  purchaseInvoiceDetail: [],
  purchaseInvoicePayment: [],
  purchaseDocument: {
    PurchaseId: "",
    Remark: "",
    FileName: "",
    fileName: "",
    remark: "",
    purchaseId: "",
  },
};

const initialState: PurchaseInvoiceFormState = {
  info: { ...initialInfo },
  editInfo: { ...initialInfo },
  error: "",
};

const tabInitialState: TabState = {
  activeTab: "1",
};

export const fetchPurchaseInvoice = createAsyncThunk<
  PurchaseInvoiceInfo,
  string,
  { rejectValue: string }
>(
  "purchaseInvoice/fetchPurchaseInvoiceInfo",
  async (id, { rejectWithValue }) => {
    try {
      const response = await PurchaseInvoiceService.getById(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to Fetch Information");
    }
  }
);

const invoiceSlice = createSlice({
  name: "purchaseInvoiceForm",
  initialState,
  reducers: {
    setInvoiceInfomationData(
      state,
      action: PayloadAction<PurchaseInvoiceInfo>
    ) {
      state.info = action.payload;
    },
    setInvoiceDetailData(
      state,
      action: PayloadAction<PurchaseInvoiceDetail[]>
    ) {
      state.info.purchaseInvoiceDetail = action.payload;
    },
    setInvoicePaymentData(
      state,
      action: PayloadAction<PurchaseInvoicePayment[]>
    ) {
      state.info.purchaseInvoicePayment = action.payload;
    },
    setInvoiceDocumentData(
      state,
      action: PayloadAction<PurchaseInvoiceDocument>
    ) {
      state.info.purchaseDocument = action.payload;
    },
    setInvoiceTotalAmount(state, action: PayloadAction<number>) {
      state.info.totalAmount=action.payload
    },
    resetpurchaseInvoiceForm(state) {
      state.info = { ...initialInfo };
      state.editInfo = { ...initialInfo };
      state.error = "";
    },
    setEditInvoiceInfomationData(
      state,
      action: PayloadAction<PurchaseInvoiceInfo>
    ) {
      state.editInfo = action.payload;
    },
    setEditInvoiceDetailData(
      state,
      action: PayloadAction<PurchaseInvoiceDetail[]>
    ) {
      state.editInfo.purchaseInvoiceDetail = action.payload;
    },
    setEditInvoicePaymentData(
      state,
      action: PayloadAction<PurchaseInvoicePayment[]>
    ) {
      state.editInfo.purchaseInvoicePayment = action.payload;
    },
    setEditInvoiceDocumentData(
      state,
      action: PayloadAction<PurchaseInvoiceDocument>
    ) {
      state.editInfo.purchaseDocument = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseInvoice.pending, (state) => {
        state.error = undefined;
      })
      .addCase(fetchPurchaseInvoice.fulfilled, (state, action) => {
        state.editInfo = action.payload;
      })
      .addCase(fetchPurchaseInvoice.rejected, (state, action) => {
        state.error =
          typeof action.payload == "string"
            ? action.payload
            : "unknown error occuredF";
      });
  },
});

const invoiceTabSlice = createSlice({
  name: "purchaseInvoiceTab",
  initialState: tabInitialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<string>) {
      state.activeTab = action.payload;
    },
    resetActiveTab(state) {
      state.activeTab = "1";
    },
  },
});
export const {
  setInvoiceInfomationData,
  setInvoiceDetailData,
  setInvoicePaymentData,
  setInvoiceDocumentData,
  setInvoiceTotalAmount,
  setEditInvoiceInfomationData,
  setEditInvoiceDetailData,
  setEditInvoicePaymentData,
  setEditInvoiceDocumentData,
  resetpurchaseInvoiceForm,
} = invoiceSlice.actions;
export const { setActiveTab, resetActiveTab } = invoiceTabSlice.actions;

export const invoiceTabReducer = invoiceTabSlice.reducer;
export const invoiceReducer = invoiceSlice.reducer;
